import type { RadarItinerary } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarItineraryTypes';
import type {
  RadarFarePolicy,
  RadarItem,
  RadarPrice,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarPricingTypes';
import type {
  Itinerary,
  Price,
  ItineraryPricingOptionItem,
  ItineraryPricingOption,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/itineraries';

const getTransferProtection = (isProtectedSelfTransfer: boolean, isSelfTransfer: boolean) => {
  if (isProtectedSelfTransfer) {
    return 'protected-self-transfer';
  }
  if (isSelfTransfer) {
    return 'self-transfer';
  }
  return '';
};

const mapPrice = (price: RadarPrice, unpricedType?: string): Price => {
  const { amount, updateStatus } = price;
  const isUnpriced = !!unpricedType && unpricedType !== '';
  if (isUnpriced) {
    return {
      update_status: updateStatus,
    };
  }
  return {
    update_status: updateStatus,
    amount,
  };
};

const mapItem = (
  item: RadarItem,
  isProtectedSelfTransfer: boolean,
  isSelfTransfer: boolean,
  farePolicy: RadarFarePolicy,
  unpricedType?: string,
): ItineraryPricingOptionItem => {
  const { agentId, price, segmentIds, url } = item;

  const pricingOptionItem: ItineraryPricingOptionItem = {
    agent_id: agentId,
    ...(farePolicy && {
      farePolicies: {
        cancellation: {
          allowed: farePolicy.isCancellationAllowed,
          partiallyAllowed: farePolicy.isPartiallyRefundable,
        },
        changes: {
          allowed: farePolicy.isChangeAllowed,
          partiallyAllowed: farePolicy.isPartiallyChangeable,
        },
      },
    }),
    price: mapPrice(price, unpricedType),
    segment_ids: segmentIds,
    transfer_protection: getTransferProtection(isProtectedSelfTransfer, isSelfTransfer),
    url: url || '',
  };

  return pricingOptionItem;
};

export const mapItineraries = (itineraries: RadarItinerary[]): Itinerary[] =>
  itineraries.map((itinerary): Itinerary => ({
    id: itinerary.id,
    token: itinerary.token,
    leg_ids: itinerary.legs.map((leg) => leg.id),
    pricing_options: itinerary.pricingOptions.map((pricingOption): ItineraryPricingOption => ({
      id: pricingOption.pricingOptionId,
      agent_ids: pricingOption.agentIds,
      isUpgradedOption: false,
      price: mapPrice(pricingOption.price, pricingOption.unpricedType),
      unpriced_type: pricingOption.unpricedType || '',
      fareAttributes: pricingOption.fareAttributes,
      items: pricingOption.items.map((item) =>
        mapItem(
          item,
          itinerary.isProtectedSelfTransfer,
          itinerary.isSelfTransfer,
          itinerary.farePolicy,
          pricingOption.unpricedType,
        ),
      ),
    })),
    airlineOffer: itinerary.airlineOffer,
    eco_contender_delta: itinerary.eco?.ecoContenderDelta,
    is_eco_contender: !!itinerary.eco?.ecoContenderDelta,
    score: itinerary.score,
  }));
